<template>
    <AppButton
        text="Builder"
        icon="builder"
        icon-size="large"
        @click="toggleProductBuilder"
        :class="{ 'border-dashed border-1': isDraft }"
        ref="pbOpen"
    />
    <Teleport to="#dialogs">
        <!-- Overlay - fades in/out -->
        <Transition name="fade">
            <div
                v-if="productBuilderVisible"
                class="fixed inset-0 top-0 bg-black/50 z-40"
                @click="toggleProductBuilder"
            ></div>
        </Transition>
        <!-- Dialog Content - animates with transition -->
        <Transition name="slide-fade-bottom">
            <div
                v-if="productBuilderVisible"
                class="fixed inset-0 bg-white top-20 left-0 w-[100vw] !right-0 flex flex-row-reverse overscroll-contain z-40 rounded-t-xl"
            >
                <button ref="pbClose" @click="toggleProductBuilder" class="flex items-center justify-center absolute top-4 right-4 z-50 p-2 rounded-full hover:bg-black/15">
                    <BaseSvg name="icon-close" />
                </button>
                <section class="pb-options h-[calc(100vh_-_var(--app-header-height))] fixed flex flex-col shrink-0 w-[470px] bg-gray-tier-3 border-l border-keyline-primary rounded-tr-xl">
                    <div v-if="isDraft" class="mx-auto">
                        <span class="px-2.5 leading-none pb-1 pt-0.5 text-center absolute top-0 left-1/2 -translate-x-1/2 uppercase tracking-wide text-xs font-semibold bg-scarlet rounded-b-lg text-white">
                            Draft
                        </span>
                    </div>
                    <div class="border-b border-gray-275 dark:border-gray-625 flex flex-col-reverse gap-2 px-8 py-10">
                        <h1 class="font-medium text-[28px] flex items-center gap-2">{{ sku }}</h1>
                        <p class="text-gray dark:text-gray-425" v-text="builder?.series?.name || 'Product Series'"></p>
                    </div>
                    <h2 class="sr-only">Configure</h2>
                    <div class="overflow-y-auto">
                        <ProductBuilderOptions
                            v-for="(config, i) in configuration.filter(c => c.selectable)"
                            :config="config"
                            :open="true /*i === 0 */"
                            :sku="sku"
                            @select="handleSelect"
                        />
                    </div>
                </section>
                <section
                    class="flex flex-col h-[calc(100vh_-_var(--app-header-height))] mr-auto w-[calc(100%_-_470px)] bg-gradient-to-b from-gray-130 to-transparent dark:from-gray-700 dark:to-gray-785 rounded-tl-xl min-w-[500px] overflow-hidden"
                    :class="{ 'opacity-50 cursor-wait': pendingRequest }"
                >
                    <!-- PB: Image(s) -->
                    <div class="p-10 mx-auto flex-1 flex flex-col items-center justify-center min-h-0">
                        <h2 class="sr-only">Image</h2>
                        <!-- IF: a product image exists. Show the product image -->
                        <img
                            v-if="productImage(configuredSummary, 0, false)"
                            :alt="'product image for ' + configuredSummary.sku"
                            :src="productImage(configuredSummary, 0, false)"
                            class="max-w-[650px]"
                            :class="{
                                'relative z-10': !productImage(configuredSummary, 0, false).startsWith('https://marketing'),
                                'relative p-32 z-10 opacity-75': productImage(configuredSummary, 0, false).startsWith('https://marketing'),
                            }">
                        <!-- ELSE IF: Show the Manufacturer Logo -->
                        <img
                            v-else-if="productImage({ vendor: builder.series.vendor_reference })"
                            class="max-h-28 max-w-xs mx-auto opacity-50"
                            :src="productImage({ vendor: builder.series.vendor_reference })"
                            :alt="configuredSummary?.vendor?.display_name + ' logo'">
                        <!-- ELSE: show an Icon-Products-Cube SVG as a placeholder -->
                        <BaseSvg v-else name="icon-products-cube" class="h-40 opacity-50 relative z-10" />
                    </div>
                    <!-- PB: Details -->
                    <div class="bg-gray-800 flex flex-col gap-4 h-[225px] shrink-0 text-white p-8 at1200:px-10 at1350:flex-row at1350:gap-16 at1350:justify-between at1350:items-start at1550:gap-24 dark:bg-white dark:text-gray-800">
                        <h2 class="sr-only">Details</h2>
                        <!-- Price and selected options -->
                        <div>
                            <!-- If price shown: -->
                            <p v-if="configuredSummary" class="flex flex-row-reverse font-semibold gap-1 items-baseline justify-end text-[2rem]">
                                <span class="text-gray-425 text-xs uppercase">Cost</span><span class="sr-only">:</span>
                                {{ showMeTheMoney(configuredSummary?.pricing_warehouse_item.list_price) }}
                            </p>
                            <p v-else>
                                Pricing unavailable.
                                <button
                                    @click="openChat()"
                                    class="text-white underline underline-offset-4 hover:text-red"
                                >
                                    {{ $t("Chat now") }}
                                </button>
                                <span>{{ $t(" to see how we can help!") }}</span>
                            </p>
                            <!-- If discount shown: -->
                            <!--<p class="flex flex-row-reverse font-semibold gap-1 items-baseline justify-end text-[2rem]"><span class="text-gray-425 text-xs uppercase">Discount</span><span class="sr-only">:</span> 50/10</p>-->
                            <p class="sr-only">Selected configuration:</p>
                            <ul class="flex flex-wrap gap-x-3 mt-2 text-gray-425 dark:text-gray">
                                <li v-for="option in selectedOptionsList">
                                    <BaseSvg name="Check" aria-hidden="true" class="size-5" />
                                    {{ option }}
                                </li>
                            </ul>
                        </div>
                        <!-- Action buttons -->
                        <div class="flex flex-wrap gap-x-4 gap-y-4 items-center at1350:pt-6 at1350:shrink-0">
                            <Counter v-model="quantity" :disabled="isLoading || !configuredCard" class="!h-12 !rounded-md overflow-hidden" :round="true" :invert="true" />
                            <button
                                class="bg-white border border-transparent flex font-medium gap-1.5 items-center px-8 py-3 rounded-md text-gray-800 hover:bg-gray-800 hover:border-white hover:text-white dark:bg-gray-800 dark:text-white dark:hover:bg-white dark:hover:border-gray-800 dark:hover:text-gray-800 disabled:opacity-50 disabled:pointer-events-none"
                                :disabled="isLoading || !configuredCard"
                                @click="addToCart"
                            >
                                <loading-circle class="size-6" v-if="isAddingToCart" />
                                <BaseSvg name="ShoppingCartThick" aria-hidden="true" class="size-6" v-else />
                                Add
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </Transition>
    </Teleport>
</template>
<script setup>
import axios from 'axios'
import _ from 'lodash'
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { AppButton, BaseSvg, Counter, LoadingCircle, ProductBuilderOptions } from "@/Components";
import { eventBus, useAddToProjectDialog, useAssetTransforms, useChat, useHelpers, useProductBuilder } from "@/Composables";

const { productImage } = useAssetTransforms();
const { toggleAddToProjectDialog } = useAddToProjectDialog();
const { showMeTheMoney } = useHelpers()
const { generateSku, getFirstEnabledOption, getSelectedOption, optionIsInvalid } = useProductBuilder()
const { openChat } = useChat();

const props = defineProps({
    builder: { type: Object, default: null },
    summary: { type: Object, default: null },
    card: { type: Object, default: null },
    availability: { type: Object, default: null },
})

const pbOpen = ref(null);
const pbClose = ref(null);
const productBuilderVisible = ref(false);
const pendingRequest = ref(null);
const isAddingToCart = ref(false);

const isLoading = computed(() => isAddingToCart.value || pendingRequest.value)
const configuredSummary = ref(props.summary)
const configuredCard = ref(props.card);
const configuredPricing = ref(props.pricing);
const configuredAvailability = ref(props.card);

const quantity = ref(1)
const sku = computed(() => generateSku(configuration))
const isDraft = computed(() => props.builder.status !== 2)

const selectedOptionsList = computed(() => configuration.value
    .filter((config) => config.selectable)
    .map((config) => config.options.find(option => option.attributes.value === config.value)?.attributes.label)
)

const toggleProductBuilder = () => {
    productBuilderVisible.value = !productBuilderVisible.value;

    // Move keyboard focus to the appropriate trigger when the Product Builder is opened or closed:
    if (productBuilderVisible.value) {
        nextTick(() => {
            pbClose.value.focus();
        });
    } else {
        nextTick(() => {
            pbOpen.value.focus();
        });
    }
};

const attributes = Object.keys(props.summary.typed_attributes).reduce((acc, key) => {
    acc[key] = props.summary.attributes[props.summary.typed_attributes[key][0]].value
    return acc
}, {})

const configuration = ref([...props.builder.components].map((component) => {
    // Pre-fill values from product attributes
    if (component.attribute) {
        const attributeValue = attributes[component.attribute] || attributes[`std_${component.attribute}`];
        const selectedOption = component.options.find((option) => option.attributes.label === attributeValue)
        component.value = selectedOption?.attributes.value ?? component.value ?? null;
    }
    return component
}))

function addToCart(event) {
    isAddingToCart.value = true
    eventBus.emit('cart:add-item', {
        sku: configuredCard.value.item_id,
        quantity: quantity.value,
    })
}

function addToProject(event) {
    toggleAddToProjectDialog(event, { card: configuredCard });
}

function handleSelect(option, source) {
    source.value = option.attributes.value

    // Adjust any invalid selections to the first valid option
    configuration.value.forEach((config) => {
        if (JSON.stringify(config) !== JSON.stringify(source)) {
            const selectedOption = getSelectedOption(config)
            if (selectedOption && optionIsInvalid(sku.value, selectedOption)) {
                const firstEnabledOption = getFirstEnabledOption(sku.value, config)
                if (firstEnabledOption) {
                    config.value = firstEnabledOption.attributes.value
                }
            }
        }
    })
}

watch(sku, _.debounce((newSku, oldSku) => {
    console.debug('SKU changed', newSku, oldSku)
    if (pendingRequest.value) {
        pendingRequest.abort()
    }

    pendingRequest.value = axios.get(route('web.catalog.products.builder', { sku: newSku }))
        .then(({ data }) => {
            configuredSummary.value = data.summary
            configuredCard.value = data.card
            configuredPricing.value = data.pricing
            configuredAvailability.value = data.availability
        })
        .catch(() => {
            configuredSummary.value = null
            configuredCard.value = null
            configuredPricing.value = null
            configuredAvailability.value = null
        })
        .finally(() => {
            pendingRequest.value = null
        })
}, 300))

onMounted(() => {
    eventBus.on('cart:product-added', () => {
        isAddingToCart.value = false
        toggleProductBuilder()
    })
})
</script>
<style>
/* Product Builder: Options */
/* Animate the opening/closing of <details> content */
.pb-options details::details-content {
    @apply h-0 overflow-clip;
    transition: content-visibility 200ms ease-in-out allow-discrete, height ease-in-out 200ms;
}
.pb-options details[open]::details-content {
    @apply h-auto;
}
/* When an option (of type: row) is hovered over and does not contain a
disabled input, apply these rules */
.pb-options__type-rows li:has(input:not([disabled])):hover {
    @apply border-gray-800 hover:bg-gray-125 dark:border-gray dark:hover:bg-transparent;
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
